import React, {useCallback, useEffect, useState} from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    TableContainer,
    TableContent,
    TableHeader,
    CELL_RENDERERS,
    THEMES,
    withTooltip,
    IconInfo,
    TextRenderer,
    TableFooter,
    Checkbox,
    StatusIndicator,
    STATUS_INDICATOR_VARIANTS,
    TOOLTIP_HORIZONTAL_ALIGNMENTS,
    TOOLTIP_VERTICAL_ALIGNMENTS
} from 'cdk-radial';
import { AgGridReact } from 'ag-grid-react';
import styled from 'styled-components';
import '../../styles/LocationsTable.scss';
import {CONSTANTS} from '../../constants';
import {
  disableNext,
  storeDBIntegrationStatuses,
  requestIntegrationStatusChange,
  resetConsentStatus,
} from '../../../../../../../redux/connectedComponent/ManageIntegrationsV2/ManageIntegrationsV2.slice';

const frameworkComponentsExtended = {
    textRenderer: TextRenderer
};

const SmallIconInfo = styled(IconInfo)`
    width: 1.125rem;
    height: 1.125rem;
    opacity: 0.7;
    margin-top: 2px;
    margin-left: 3px;
`;

const VerticalCenterContainer = styled.div`
  display: flex;
  align-items: center;
`;

const FullWidthCellBase = styled(VerticalCenterContainer)`
    width: 100%;
    justify-content: space-between;
`;

const CenterCellBase = styled(FullWidthCellBase)`
    justify-content: center;
`;

function TableHeaderWithInfo({ displayName, explanatoryText }) {
    const TooltipWrapper = withTooltip(SmallIconInfo, {
      id: 'header-info-tooltip',
      text: explanatoryText,
      theme: THEMES.LIGHT
    });

    return <FullWidthCellBase>
      <span role='columnheader' className='ag-header-cell-text'>{displayName}</span>
      <TooltipWrapper />
    </FullWidthCellBase>;
}

const STATUS_TO_REQUESTED_STATUS_MAPPING = {
    enabled: 'enable',
    disabled: 'disable',
};

const STATUS_TO_CHECKBOX_MAPPING = {
    enable: true,
    enabled: true,
    disable: false,
    disabled: false,
};

//need to apply in-line style as Status radial component does not take classname
const statusStyle = {width: "70px", justifyContent: "center", textTransform: "capitalize"};

export const IntegrationSwitch = ({data = {}}) => {
  const dispatch = useDispatch();
  const {entityId, index, disabled} = data;
  const {requestedIntegrationStatuses} = useSelector(state => state.ManageIntegrationsV2Slice);
  const userRequestedChange = requestedIntegrationStatuses[entityId];  //enable or disable
  const [checked, setChecked] = useState(userRequestedChange ? STATUS_TO_CHECKBOX_MAPPING[userRequestedChange] : data.checked);

  /**
     * Gets invoked when integration status checkboxes are checked/unchecked
     * Identifies entities for which integration status change is requested
     * Dispatches action to store such entityIds to redux store
     * @param e: event
     * @param entityId: entityId
     */
  const onToggle = useCallback((e, entityId) => {
    setChecked(e.target.checked);
    const requestedStatus = e.target.checked ? CONSTANTS.enable : CONSTANTS.disable;
    dispatch(requestIntegrationStatusChange({entityId, requestedStatus}));
  }, []);

  return <CenterCellBase>
    <Checkbox
      data-testid={`integration-checkbox-${index}`}
      key={index}
      checked={checked}
      isDisabled={disabled}
      hideLabel
      onChange={e => onToggle(e, entityId)}
    />
  </CenterCellBase>;
};

/**
 * Render framework component for API provisioning status
 * Reders <StatusIndicator /> radial component with supplied value
 * Provisioning status defaults to 'Unknown' if no value is supplied
 *
 * @param {value} - API Provisioning Status -> active, inactive or undefined
 */
export const Status = ({ value }) => {
    function getProvisioningStatus() {
      switch (value) {
        case 'inactive':
          return 'The integration is disabled, or the API Publisher has not started provisioning.';
        case 'pending':
          return 'The API Publisher is working on provisioning. The app may still receive events if the API Publisher sends them.';
        case 'active':
          return 'The API Publisher has finished provisioning and is able to send events.';
        default:
          return 'Unknown';
      }
    }

    const tooltipProps = {
      id: 'tooltip-id-2',
      text: getProvisioningStatus(),
      theme: THEMES.LIGHT,
    };

    const ComponentToRender = ({ ...props }) => {
      return (
        <StatusIndicator
          label={value || CONSTANTS.DEFAULT_PROVISIONING_STATUS}
          variant={
            STATUS_INDICATOR_VARIANTS[
              CONSTANTS.STATUS_TO_INDICATOR_VARIANT_MAP[value] ||
                CONSTANTS.DEFAULT_INDICATOR_VARIANT
            ]
          }
          style={statusStyle}
          {...props}
        />
      );
    };

    const ComponentWithTooltip = withTooltip(ComponentToRender, tooltipProps);

    const ToolTip = () => {
      return (
        <div css="display: flex;">
          <ComponentWithTooltip
            horizontalAlignment={TOOLTIP_HORIZONTAL_ALIGNMENTS.LEFT}
            marginAroundElement={8}
            verticalAlignment={TOOLTIP_VERTICAL_ALIGNMENTS.RIGHT}
            value={value}
          />
        </div>
      );
    };
    return <ToolTip />;
  };

const columns = [
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'location',
    headerName: CONSTANTS.DEALERSHIP_LOCATION,
    width: 400,
    cellClass: 'tooltip-truncate',
    tooltipField: 'location',
  },
  {
    cellRenderer: CELL_RENDERERS.TEXT,
    field: 'address',
    headerName: CONSTANTS.ADDRESS,
    width: 400,
    cellClass: 'tooltip-truncate',
    tooltipField: 'location',
  },
  {
    cellRendererFramework: IntegrationSwitch,
    field: 'status',
    headerName: CONSTANTS.INTEGRATION_STATUS,
    filter: false,
    minWidth: 180,
    width: 150,
    resizable: false,
    headerComponentFramework: TableHeaderWithInfo,
    headerComponentParams: {
      explanatoryText: CONSTANTS.INTEGRATION_STATUS_DESCRIPTION,
    },
    suppressSizeToFit: true,
    tooltipField: 'description',
  },
];

const suppressColumnVirtualisation = process.env.NODE_ENV === "test";

const provisioningStatusColumn = [{
    cellRendererFramework: Status,
    field: 'apiProvisioningStatus',
    headerName: CONSTANTS.PROVISIONING_STATUS,
    minWidth: 180,
    width: 180,
    headerComponentFramework: TableHeaderWithInfo,
    headerComponentParams: {
        explanatoryText: CONSTANTS.PROVISIONING_STATUS_DESCRIPTION
    },
    suppressSizeToFit: true,
},]

export const DealershipsTable = ({apiName, apiType, integrationsDataPerLocation = [], isNewApi}) => {
    const dispatch = useDispatch();

    //map of entityId to entity name
    const {
        entityIdNameMapping,
        entityIdAddressMapping
    } = useSelector(state => state.loginDetailsSlice);

    //User actions: entities that have been selected for integration status change
    const {requestedIntegrationStatuses} = useSelector(state => state.ManageIntegrationsV2Slice);

    //local state
    const [gridApi, setGridApi] = useState({});
    const [rowData, setRowData] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [columnDefs, setColumns] = useState(
        apiType === CONSTANTS.ASYNC_API ?
        [
            ...columns,
            ...provisioningStatusColumn,
        ] :
        columns
    );

    /**
     * Sets table data
     * Checkboxes for integration status are set according to the status value('enabled' or 'disabled') present in the db
     * Retains user actions on the checkboxes i.e. if user toggles any checkbox then its value is pulled from the redux state
     * @param gridParams: Grid API
     */
    const onGridReady = useCallback(gridParams => {
        setGridApi(gridParams.api);
        gridParams.api.sizeColumnsToFit();
        const dbStatuses = {};
        const data = integrationsDataPerLocation.map((integration, index) => {
            dbStatuses[integration.entityId] = {
              status: integration.status || CONSTANTS.DISABLED,
              requestedStatus: integration.requested_status,
            };
            const {entityId, status, requested_status, apiProvisioningStatus} = integration;
            //requestedIntegrationStatuses represent user actions taken on integration status checkbox
            const integrationStatus = requestedIntegrationStatuses[entityId] || status;
            const checked = requested_status === CONSTANTS.enable;
            const disabled = STATUS_TO_REQUESTED_STATUS_MAPPING[status] !== requested_status;
            const description = disabled ? `Pending request for ${checked ? CONSTANTS.enable : CONSTANTS.disable}` : null;
            return {
                location: entityIdNameMapping[entityId],
                address: entityIdAddressMapping[entityId],
                entityId,
                status: integrationStatus,
                index,
                requestedStatus: requested_status,
                disabled,
                checked,
                apiProvisioningStatus,
                description,
            };
        });
        setRowData(data);
        dispatch(storeDBIntegrationStatuses(dbStatuses));
    }, []);

    useEffect(() => {
        Object.keys(requestedIntegrationStatuses).length ? dispatch(disableNext(false)) : dispatch(disableNext(true));
    }, [requestedIntegrationStatuses]);

    useEffect(() => {
      dispatch(resetConsentStatus());
    }, []);

    return (
      <TableContainer className="locations-table ag-theme-balham">
        <TableHeader
          headerTitle={apiName}
          isDownloadable={false}
          isPrintable={false}
          isSearchable={true}
          searchInputValue={searchValue}
          onSearchInputChange={(v) => {
            setSearchValue(v);
            gridApi.setQuickFilter(v);
            // Refresh is needed for filtered cells highlighting to work properly
            gridApi.refreshCells({
              force: true,
            });
          }}
        />
        <TableContent>
          <AgGridReact
            // force grid to rebuild when columns change
            key={`${columnDefs.map((c) => c.headerName)}`}
            columnDefs={columnDefs}
            rowData={rowData}
            frameworkComponents={frameworkComponentsExtended}
            onGridReady={onGridReady}
            rowHeight={40}
            suppressRowClickSelection={true}
            pagination={true}
            paginationPageSize={10}
            suppressColumnVirtualisation={suppressColumnVirtualisation}
          />
        </TableContent>
        <TableFooter />
      </TableContainer>
    );
};
